<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import { MagazineSearchBarProps } from "./MagazineSearchBar.props"
import { MAGAZINE_CATEGORIES_MODAL } from "~/utils/filters"
import { customPaths } from "~/utils/constants"
import { checkEnvSite } from "~~/server/utils/getAlgoliaConfig"
import { buildExcludeFilter, EAnalyticsTag, getUserId } from "~/utils/algolia"

import { SearchResponse } from "@nuxtjs/algolia"
import { ResultsProps } from "~/components/ProductTile/Overall/Results.props"
import { ButtonProps } from "~/components/Utils/Button.props"
import MagazineSuggestionsGroups from "./Utils/MagazineSuggestionsGroups.vue"
import { useMagazineQuerySuggestions } from "~/composables/useMagazineQuerySuggestions"
import { useMagazineRecentSearches } from "~/composables/useMagazineRecentSearches"
import { useMagazineRecentArticles } from "~/composables/useMagazineRecentArticles"

const props = withDefaults(defineProps<MagazineSearchBarProps>(), {
  name: "magazine-search",
  disabled: false,
  preloadIcons: false,
  modelValue: ""
})

const emit =
  defineEmits<{
    (e: "onSearch", query: string): void
    (e: "update:modelValue", value: string): void
  }>()

const { t } = useI18n()
const { querySuggestions, isLoading: querySuggestionsLoading } =
  useMagazineQuerySuggestions()
const { recentSearches, addRecentSearch } = useMagazineRecentSearches()
const { recentArticles } = useMagazineRecentArticles()

const indexName = ref(`TOP_MAGAZINE_ARTICLES${checkEnvSite()}`)
const algoliaClient = useAlgoliaRef()
const loadingSearchResults = useState("loading-search-results", () => false)

const searchValue = ref(props.modelValue)
const isFocused = ref(false)
const isUserInteraction = ref(false)
const isAutoCompleting = ref(false)
const searchbar = ref()
const inputSearch = ref()
const isOverlayOpen = ref(false)
const user = getUserId()

// Combine internal disabled state with props disabled
const isDisabled = computed(() => props.disabled)

// Dropdown logic (copied from ListPosts.vue)
const { isOpen, switchState: openMagazineCategories } = useUIState(
  MAGAZINE_CATEGORIES_MODAL
)

const dropdownArgs = reactive({
  label: t("magazine.categories"),
  openModal: false,
  dropdownMenu: null
})

const { data: getCategoriesOfMagazine, error: errorGetCategoriesOfMagazine } =
  await useFetch(`/api/magazine/getCategories`, {
    method: "GET"
  })

if (errorGetCategoriesOfMagazine.value) {
  console.warn(
    "error get categories of Magazine",
    errorGetCategoriesOfMagazine.value
  )
} else if (
  getCategoriesOfMagazine.value &&
  Array.isArray(getCategoriesOfMagazine.value)
) {
  // @ts-ignore
  dropdownArgs.dropdownMenu = [
    {
      label: t("magazine.allPosts"),
      value: customPaths.magazineAllPosts
    },
    ...getCategoriesOfMagazine.value.map(
      (category: { title: string; slug: string }) => ({
        label: category.title,
        value: category.slug
      })
    )
  ]
}

const searchQueries = computed(() => [
  {
    indexName: indexName.value,
    params: {
      query: searchValue.value,
      hitsPerPage: 3,
      clickAnalytics: true,
      analyticsTags: [EAnalyticsTag.autocomplete],
      highlightPostTag: "__/ais-highlight__",
      highlightPreTag: "__ais-highlight__",
      userToken: user,
      ...buildExcludeFilter({ excludeOutOfMarket: true })
    }
  },
  {
    indexName: indexName.value,
    params: {
      query: searchValue.value,
      hitsPerPage: 0,
      facets: ["categories.name"],
      maxValuesPerFacet: 3,
      clickAnalytics: true,
      analyticsTags: [EAnalyticsTag.autocomplete],
      userToken: user,
      ...buildExcludeFilter({ excludeOutOfMarket: true })
    }
  }
])

const algoliaSearchResults: globalThis.ComputedRef<
  Promise<[SearchResponse<ResultsProps>, SearchResponse<any>] | null>
> = computed(async () => {
  const val = searchValue.value || ""
  if (!val || val.length < 3) {
    return null
  }

  loadingSearchResults.value = true
  const results = await algoliaClient.search(searchQueries.value)
  loadingSearchResults.value = false

  return results.results as [SearchResponse<ResultsProps>, SearchResponse<any>]
})

const suggestions = computedAsync(async () => {
  const results = await algoliaSearchResults.value
  if (!results) return []

  const articleResults = results[0]

  const articleSuggestions = transformAlgoliaHitsToButtons(
    articleResults?.hits || []
  )

  return articleSuggestions
})

const categorySuggestions = computedAsync(async () => {
  const results = await algoliaSearchResults.value
  if (!results) return []

  const facetResults = results[1]

  const categorySuggestions = transformCategoryFacetsToButtons(
    facetResults?.facets?.["categories.name"] || {}
  )

  return categorySuggestions
})

const transformAlgoliaHitsToButtons = (hits: any[]): ButtonProps[] => {
  return hits.map((hit) => ({
    text: hit.title || "",
    objectID: hit.objectID || "",
    path:
      hit.slug && hit.category?.slug
        ? `/top-magazine/${hit.category.slug}/${hit.slug}`
        : `/top-magazine/ricerca?query=${encodeURIComponent(hit.title || "")}`,
    isSelectable: false
  }))
}

const transformCategoryFacetsToButtons = (
  facets: Record<string, number>
): ButtonProps[] => {
  return Object.entries(facets)
    .filter(([categoryName, count]) => count > 0)
    .map(([categoryName, count]) => {
      // Trova la categoria corrispondente nel dropdown per ottenere lo slug
      const categoryItem = dropdownArgs.dropdownMenu?.find(
        (item: any) => item.label === categoryName
      )

      return {
        text: `${categoryName}`,
        objectID: `category-${categoryName}`,
        path: categoryItem?.value
          ? `${categoryItem.value}`
          : `/top-magazine/ricerca?query=${encodeURIComponent(categoryName)}`,
        isSelectable: false
      }
    })
}

// Watch for modelValue changes
watch(
  () => props.modelValue,
  (newValue) => {
    searchValue.value = newValue || ""
  }
)

// Watch for searchValue changes and emit update
watch(searchValue, (newValue) => {
  emit("update:modelValue", newValue)
})

// Track mouse and keyboard interactions
const handleUserInteraction = () => {
  isUserInteraction.value = true
}

const handleFocusIn = () => {
  // Handle focus only if there has been a user interaction
  if (isUserInteraction.value) {
    isFocused.value = true
    isOverlayOpen.value = true
    isUserInteraction.value = false // Reset after use
  } else {
    // Remove focus from active element if it's not a user interaction
    if (
      document.activeElement &&
      document.activeElement instanceof HTMLElement
    ) {
      document.activeElement.blur()
    }
  }
}

const handleFocusOut = () => {
  isFocused.value = false
  // Delay closing to allow clicks on overlay content
  setTimeout(() => {
    // Don't close if we're in the middle of autocompleting
    if (!isAutoCompleting.value) {
      isOverlayOpen.value = false
    }
  }, 150)
}

const handleCloseOverlay = () => {
  isOverlayOpen.value = false
  isFocused.value = false
  const activeElement = document.activeElement as HTMLElement
  activeElement?.blur()
}

const cleanValue = () => {
  searchValue.value = ""
}

const handleSearch = () => {
  if (searchValue.value.trim()) {
    const query = searchValue.value.trim()

    // Add to recent searches
    addRecentSearch(query)

    // Emit the event for compatibility
    emit("onSearch", query)

    // Navigate to the search page with query parameter
    navigateTo({
      path: "/top-magazine/ricerca",
      query: { query }
    })
  }
}

const triggerClickButton = () => {
  handleSearch()
}

// Handle all search clicks (recent, popular, suggestions)
const handleSearchClick = (item: ButtonProps) => {
  // If the item has a path, navigate directly to it
  if (item.path && typeof item.path === "string" && item.path.trim()) {
    handleCloseOverlay()
    navigateTo(item.path)
    return
  }

  // Otherwise, autocomplete and then execute search
  const query = item.text || ""
  if (query.trim()) {
    searchValue.value = query
    handleSearch()
  }
}

// Dropdown functions (copied from ListPosts.vue)
const openModal = () => {
  dropdownArgs.openModal = true
}

const closeModal = (value: boolean) => {
  dropdownArgs.openModal = value
}

const goToCategoryMagazine = (e: any) => {
  navigateTo(e.value)
  openMagazineCategories(false)
}

// Handle ESC key to close overlay
onKeyStroke(
  "Escape",
  (e) => {
    if (isOverlayOpen.value) {
      handleCloseOverlay()
    }
  },
  { dedupe: false }
)
</script>

<template>
  <div
    class="
      negative-margined
      relative
      flex flex-col
      gap-4
      bg-default-green-10
      px-4
      pb-10
      pt-8
      md:mx-0
    "
  >
    
<FormKitLazyProvider config-file="true">
<div class="padded">
      <h2 class="hyppo-bold md:elephant-bold text-black-main mb-4 text-center">
        {{ t("magazine.search.title") }}
      </h2>
      <div class="flex flex-col gap-4 md:flex-row md:items-center">
        <div class="search-searchbar relative flex-1" ref="searchbar">
          <FormKit
            ref="inputSearch"
            :id="name"
            :name="name"
            type="text"
            autocomplete="off"
            v-model="searchValue"
            :placeholder="placeholder || t('magazine.search.placeholder')"
            :disabled="isDisabled"
            :classes="{
              outer: 'relative',
              wrapper: 'relative bg-inherit',
              label: 'sr-only',
              input: {
                'pl-4 text-ellipsis w-full input-text__textfield bg-white border disabled:border-tiffany-main rounded-[100px] pr-12 placeholder:!text-black-80 placeholder:select-none text-black-main': true,
                'text-black-20 placeholder:!text-black-20': Boolean(isDisabled),
                'border-orange-main border-2 py-[11px]': Boolean(isFocused),
                'border-tiffany-main py-[12px]': !isFocused,
                'pr-[75px]': Boolean(searchValue)
              }
            }"
            @focusin="handleFocusIn"
            @focusout="handleFocusOut"
            @keyup.enter="triggerClickButton"
            @mousedown="handleUserInteraction"
            @keydown="handleUserInteraction"
          >
            <template #suffixIcon>
              <button
                v-if="searchValue"
                @click="cleanValue"
                :disabled="isDisabled"
                class="absolute right-12 top-1/2 -translate-y-1/2"
              >
                <UtilsIcon
                  name="CloseCircleFull.svg"
                  :preload="preloadIcons ? true : undefined"
                  :fetch-priority="preloadIcons ? 'high' : undefined"
                  color="black-40"
                  class="h-6 w-6"
                />
              </button>
              <button
                :disabled="isDisabled"
                @click="handleSearch"
                class="
                  bg-green-main
                  absolute
                  right-[3px]
                  top-1/2
                  mr-[2px]
                  flex
                  -translate-y-1/2
                  items-center
                  justify-center
                  rounded-full
                  p-[7px]
                "
              >
                <UtilsIcon
                  name="Search.svg"
                  class="h-6 w-6 flex-shrink-0"
                  color="white"
                  :preload="preloadIcons ? true : undefined"
                  :fetch-priority="preloadIcons ? 'high' : undefined"
                />
              </button>
            </template>
          </FormKit>

          <Transition name="opacity">
            <div
              v-show="isOverlayOpen"
              class="
                magazine-search-overlay
                bg-white
                absolute
                left-0
                top-full
                z-40
                mt-1
                w-full
                rounded-[16px]
                p-6
                shadow-lg
                md:w-[calc(100%+40px)]
              "
            >
              <MagazineSuggestionsGroups
                :query-state="searchValue"
                :lastSearch="recentSearches"
                :lastViewed="recentArticles"
                :suggestions="suggestions"
                :querySuggestions="querySuggestions"
                :querySuggestionsLoading="querySuggestionsLoading"
                :categorySuggestions="categorySuggestions"
                @clickSuggestions="handleSearchClick"
              />
            </div>
          </Transition>
        </div>

        <!-- Dropdown filters -->
        <div
          class="md:border-black-20 md:border-b-[1px]"
          v-if="dropdownArgs && dropdownArgs.dropdownMenu"
        >
          <div class="hidden md:block">
            <span class="mouse text-black-80 mr-3 uppercase">{{
              t("magazine.plp.filterBy")
            }}</span>
            <FiltersUtilsDropdownWithoutAlgolia
              :text="dropdownArgs.label"
              :opened="dropdownArgs.openModal"
              attachTo="right"
              @onOpen="openModal"
              @onClose="() => closeModal(false)"
              class="z-low-ground"
            >
              <div class="space-y-1">
                <AppLink
                  v-for="{ label, value } in dropdownArgs.dropdownMenu"
                  :key="value"
                  :to="value"
                  class="
                    mouse
                    hover:mouse-medium
                    flex
                    min-w-[150px]
                    cursor-pointer
                    justify-between
                    p-2
                  "
                >
                  {{ label }}
                </AppLink>
              </div>
            </FiltersUtilsDropdownWithoutAlgolia>
          </div>
          <div class="negative-margined block md:hidden">
            <FiltersUtilsMobileFiltersBarMagazine
              :sorting-open="isOpen"
              :bg="false"
              @onSearch="() => openMagazineCategories(true)"
              class="mt-3"
            />
            <FiltersUtilsModalsMagazineCategories
              v-if="dropdownArgs.dropdownMenu"
              :items="dropdownArgs.dropdownMenu"
              @onSelectItem="goToCategoryMagazine"
              @onClose="() => openMagazineCategories(false)"
            />
          </div>
        </div>
      </div>
    </div>
</FormKitLazyProvider>

  </div>
</template>

<style scoped>

.opacity-enter-active,
.opacity-leave-active {
  transition: opacity 0.3s ease;
}

.opacity-enter-from,
.opacity-leave-to {
  opacity: 0;
}

</style>
